import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const FOR_YOU = [
  "You want to master one market instead of dabbling in ten",
  "You want a daily plan — bias, levels and events — before you trade",
  "You care about the reasoning behind a call, not just the call itself",
  "You can commit 30 days to a structured, consistent routine",
];

const NOT_FOR_YOU = [
  "You're looking for guaranteed returns — no honest desk can promise that",
  "You want lottery-style leverage bets, not planned setups",
  "You'd rather chase tips than build a repeatable process",
  "You're not willing to follow risk rules on every single trade",
];

const WhyJoin = () => (
  <section id="why-join" className="relative py-28 sm:py-36" data-testid="why-join-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">(04) — The Fit</p>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a] sm:text-5xl">
          Why should you <span className="font-accent text-grad-aurora">join?</span>
        </h2>
        <p className="mt-4 text-base text-slate-600 sm:text-lg">
          An honest answer: this room isn't for everyone. Here's how to know before you pay.
        </p>
      </motion.div>

      <div className="grid gap-5 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="glass card-aurora rounded-3xl p-8"
          data-testid="why-join-for-you"
        >
          <h3 className="font-sub text-lg font-semibold uppercase tracking-wide text-[#0a0a0a]">
            This room is for you if
          </h3>
          <ul className="mt-6 space-y-4">
            {FOR_YOU.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700 sm:text-base">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#15803d]">
                  <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.12 }}
          className="glass rounded-3xl p-8"
          data-testid="why-join-not-for-you"
        >
          <h3 className="font-sub text-lg font-semibold uppercase tracking-wide text-slate-500">
            It's not for you if
          </h3>
          <ul className="mt-6 space-y-4">
            {NOT_FOR_YOU.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-slate-500 sm:text-base">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100">
                  <X className="h-3.5 w-3.5 text-rose-500" strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mx-auto mt-10 max-w-xl text-center font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-400"
        data-testid="why-join-note"
      >
        No profit promises. No hype. Just daily structure and a room that shows up.
      </motion.p>
    </div>
  </section>
);

export default WhyJoin;
